import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import RegionBoundaryMap from '../../../components/regions/RegionBoundaryMap';
import PolygonEditor from '../../../components/maps/PolygonEditor';
import client from '../../../lib/feathers';

// Create or edit a region.
//
// The shape is seeded from the appellations it covers rather than drawn
// freehand: a buffered union gets most of it in one query, and the preview
// reports what that shape would actually catch, so the buffer is tuned against
// a number instead of by eye. See docs/PUBLIC_SITE_EXPLORE_PLAN.md.

const BUFFER_CHOICES = [
  { meters: 0, label: 'None' },
  { meters: 1600, label: '1 mile' },
  { meters: 4800, label: '3 miles' },
  { meters: 8000, label: '5 miles' },
  { meters: 16000, label: '10 miles' },
];

export default function RegionEdit() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const isNew = !rid || rid === 'new';

  const [form, setForm] = useState({ name: '', description: '', country: '', status: 'draft' });
  const [boundary, setBoundary] = useState(null);
  const [savedBoundary, setSavedBoundary] = useState(null);
  const [pins, setPins] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [appellations, setAppellations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [buffer, setBuffer] = useState(4800);
  const [preview, setPreview] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await client.service('admin/region').get(rid, { query: { $withPins: true } });
      setForm({
        name: r.name,
        description: r.description || '',
        country: r.country || '',
        status: r.status,
      });
      setSavedBoundary(r.boundary || null);
      setPins(r.pins || []);
      const list = await client.service('admin/region').find({
        query: { id: rid, $withCounts: true },
      });
      const row = (list.data || list)[0];
      setCounts(row ? row.counts : null);
    } catch (e) {
      setError(e.message || 'Could not load this region');
    } finally {
      setLoading(false);
    }
  }, [rid, isNew]);

  useEffect(() => { load(); }, [load]);

  const searchAppellations = useCallback(async (term) => {
    if (!term || term.length < 2) { setAppellations([]); return; }
    const res = await client.service('admin/appellation').find({
      query: { name: { $iLike: `%${term}%` }, $limit: 25, $sort: { name: 1 } },
    });
    setAppellations(res.data || res);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchAppellations(search), 250);
    return () => clearTimeout(t);
  }, [search, searchAppellations]);

  const selectedIds = useMemo(() => selected.map((a) => a.id), [selected]);

  const toggle = (a) => {
    setSelected((prev) => (prev.some((x) => x.id === a.id)
      ? prev.filter((x) => x.id !== a.id)
      : [...prev, a]));
  };

  const seed = async () => {
    if (selectedIds.length === 0) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await client.service('admin/region-seed').create({
        appellationIds: selectedIds, bufferMeters: buffer,
      });
      setBoundary(res.boundary);
      setPreview(res.preview);
    } catch (e) {
      setError(e.message || 'Could not build a shape from those appellations');
    } finally {
      setSeeding(false);
    }
  };

  // Counts follow the shape. Debounced because a drag can end many times in a
  // few seconds and each measure is two spatial queries.
  const measureTimer = useRef(null);
  const onBoundaryChange = useCallback((next) => {
    setBoundary(next);
    if (measureTimer.current) clearTimeout(measureTimer.current);
    if (!next) { setPreview(null); return; }
    measureTimer.current = setTimeout(async () => {
      try {
        const res = await client.service('admin/region-seed').create({ boundary: next });
        setPreview(res.preview);
      } catch (e) {
        setError(e.message || 'Could not measure that shape');
      }
    }, 400);
  }, []);

  // A drawn blob is an instruction. The server unions or subtracts it, tidies
  // the result back under the vertex budget, and returns the new shape, so an
  // edit cannot leave the outline overlapping itself or growing unboundedly.
  const onReshape = useCallback(async (area, mode) => {
    setError(null);
    try {
      const current = boundary || savedBoundary;
      if (!current) { setError('Seed a shape before editing it.'); return; }
      const res = await client.service('admin/region-seed').create({
        boundary: current,
        ...(mode === 'add' ? { addArea: area } : { removeArea: area }),
      });
      setBoundary(res.boundary);
      setPreview(res.preview);
    } catch (e) {
      setError(e.message || 'Could not apply that area');
    }
  }, [boundary, savedBoundary]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      // Only sent when a shape was seeded this session. A patch that omits it
      // leaves the stored boundary alone, which is what editing a name should
      // do.
      if (boundary) payload.boundary = boundary;

      if (isNew) {
        const created = await client.service('admin/region').create(payload);
        navigate(`/admin/regions/${created.id}`);
      } else {
        await client.service('admin/region').patch(rid, payload);
        setBoundary(null);
        setPins([]);
        setPreview(null);
        await load();
      }
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const shown = preview || counts;

  return (
    <Page title={isNew ? 'New region' : form.name || 'Region'}>
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Regions', href: '/admin/regions' },
            { name: isNew ? 'New' : form.name || 'Region' },
          ]}
          />
        </nav>
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading && <p className="mt-4 text-sm text-gray-500">Loading...</p>}

      {!loading && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Details, then the shape controls, then the map showing the result
              of both. The map is last because it is the outcome, not an input. */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow sm:rounded-lg p-6 space-y-4">
              <div>
                <label htmlFor="region-name" className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  id="region-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
                />
                {!isNew && (
                  <p className="mt-1 text-xs text-gray-500">
                    Renaming does not move the URL. The slug is set once, when
                    the region is created.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="region-description" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="region-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
                />
              </div>

              <div>
                {/* Explore groups regions by country. Without it the index is a
                    flat pile of Napa, Okanagan, Finger Lakes and Yadkin Valley
                    with nothing to sort them by. Deliberately typed rather than
                    derived from the polygon: a region can straddle a border,
                    and guessing is worse than leaving it ungrouped. */}
                <label htmlFor="region-country" className="block text-sm font-medium text-gray-700">Country</label>
                <input
                  id="region-country"
                  type="text"
                  maxLength={2}
                  placeholder="US"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })}
                  className="mt-1 block w-24 border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm uppercase"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Two-letter code, the same as an appellation uses. US, CA, FR. Leave blank to stay ungrouped.
                </p>
              </div>

              <div>
                <label htmlFor="region-status" className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  id="region-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
                >
                  <option value="draft">Draft, not shown publicly</option>
                  <option value="active">Active, live on the public site</option>
                </select>
              </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900">Shape</h3>
              <p className="mt-1 text-sm text-gray-500">
                Pick the appellations this region covers and seed a shape from
                them. The buffer widens it, which sweeps in nearby wineries that
                belong to no appellation.
              </p>

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search appellations"
                className="mt-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-800 focus:border-red-800 sm:text-sm"
              />

              {appellations.length > 0 && (
                <ul className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                  {appellations.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => toggle(a)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between"
                      >
                        <span>{a.name}</span>
                        <span className="text-gray-400">
                          {selectedIds.includes(a.id) ? 'Selected' : a.designationType}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {selected.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggle(a)}
                      className="px-2 py-1 text-xs rounded-full bg-red-50 text-red-800 border border-red-200"
                    >
                      {`${a.name} x`}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <label htmlFor="region-buffer" className="text-sm text-gray-700">Buffer</label>
                <select
                  id="region-buffer"
                  value={buffer}
                  onChange={(e) => setBuffer(Number(e.target.value))}
                  className="border-gray-300 rounded-md shadow-sm sm:text-sm"
                >
                  {BUFFER_CHOICES.map((b) => (
                    <option key={b.meters} value={b.meters}>{b.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={seed}
                  disabled={selected.length === 0 || seeding}
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                >
                  {seeding ? 'Building...' : 'Seed shape'}
                </button>
              </div>

              {boundary && (
                <p className="mt-3 text-xs text-gray-500">
                  A new shape is loaded and will be saved with this region.
                </p>
              )}
            </div>

            <div className="bg-white shadow sm:rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  {editing ? 'Editing the shape' : 'Shape and what it holds'}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditing((v) => !v)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  {editing ? 'Done editing' : 'Edit the outline'}
                </button>
              </div>

              {editing ? (
                <PolygonEditor
                  boundary={boundary || savedBoundary}
                  onBoundaryChange={onBoundaryChange}
                  onReshape={onReshape}
                  pins={pins}
                  referenceLabel="Dots are the wineries we know about, so you can see what a change takes in or leaves out."
                  downloadName="region-boundary.geojson"
                />
              ) : (
                <RegionBoundaryMap
                  boundary={boundary || savedBoundary}
                  pins={boundary ? [] : pins}
                  height="520px"
                />
              )}

              {preview && preview.valid === false && (
                <p className="mt-2 text-sm text-red-600">
                  {`This shape crosses itself and cannot be saved. ${preview.invalidReason || ''}`}
                </p>
              )}
              {boundary && !editing && (
                <p className="mt-2 text-xs text-gray-500">
                  Showing an unsaved shape. Save to see the wineries it holds.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white shadow sm:rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900">
                {preview ? 'This shape would catch' : 'Currently catches'}
              </h3>
              {shown ? (
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Wineries</dt>
                    <dd className="font-medium text-gray-900">{shown.wineries}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Appellations</dt>
                    <dd className="font-medium text-gray-900">{shown.appellations}</dd>
                  </div>
                  {shown.diagonalMiles !== undefined && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Across</dt>
                      <dd className="font-medium text-gray-900">{`${shown.diagonalMiles} mi`}</dd>
                    </div>
                  )}
                  {shown.vertices !== undefined && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Points in the shape</dt>
                      <dd className="font-medium text-gray-900">{shown.vertices}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="mt-2 text-sm text-gray-500">No shape yet.</p>
              )}
              <p className="mt-3 text-xs text-gray-500">
                Counts are worked out from the shape each time, so they move as
                listings are added or geocoded.
              </p>
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving || !form.name || (preview && preview.valid === false)}
              className="w-full px-4 py-2 rounded-md text-sm font-medium text-white bg-red-800 hover:bg-red-900 disabled:opacity-40"
            >
              {saving ? 'Saving...' : 'Save region'}
            </button>
          </div>
        </div>
      )}
    </Page>
  );
}
